import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Select } from '@mantine/core';
import { UserEndpoints, RouterPaths } from '@/services/shared/ApiEndpoints';
import { UserService } from '@/services/UserService';
import { NotificationType } from '@/types/Common';
import { notify } from '@/utils/notify';
import { API } from '@/utils/api';
import { useAuth } from '@/services/auth/AuthContext';

interface TenantData {
  currentTenantId: string;
  whitelist: { uuid: string; name: string }[];
}

const SectionListRedirectMap: Record<string, string> = {
  wallets: RouterPaths.WALLET_LIST,
  agreements: RouterPaths.AGREEMENTS_LIST,
  users: RouterPaths.USERS_LIST,
  newsletter: RouterPaths.NEWSLETTER_LIST,
  prices: RouterPaths.PRICES_AND_CHARTS,
  dictionary: RouterPaths.DICTIONARY_LIST,
  default: RouterPaths.DASHBOARD,
};

export const TenantSelector: React.FC = () => {
  const [tenants, setTenants] = useState<{ value: string; label: string }[]>([]);
  const [currentTenant, setCurrentTenant] = useState<string | null>('');
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const router = useRouter();
  const { realmRoles } = useAuth();
  const isAdmin = realmRoles.includes('realm_administrator');

  useEffect(() => {
    API.get<TenantData>(UserEndpoints.TENANT_CONTEXT)
      .then((r) => {
        const { currentTenantId, whitelist } = r.data;
        if (whitelist && whitelist.length > 0) {
          setTenants(whitelist.map((t) => ({ value: t.uuid, label: t.name })));
          setCurrentTenant(currentTenantId || '');
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      })
      .catch((e) => {
        console.error('Failed to fetch tenant context:', e);
        setIsVisible(false);
      });
  }, []);

  const handleTenantChange = async (newTenantId: string | null) => {
    try {
      const response = await UserService.switchTenant(newTenantId);

      if (response.success) {
        setCurrentTenant(newTenantId);

        const currentSection = router.pathname.split('/')[1];
        const fallbackPath =
          SectionListRedirectMap[currentSection] || SectionListRedirectMap.default;

        window.location.href = fallbackPath;
      } else {
        throw new Error(response.error || 'Nie udało się zmienić tenanta');
      }
    } catch (error: any) {
      console.error('Błąd podczas zmiany tenanta:', error);
      notify(NotificationType.ERROR, error.message || 'Nie udało się zmienić tenanta');
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Select
      placeholder={isAdmin && !currentTenant ? 'Wszyscy klienci' : 'Wybierz klienta'}
      data={tenants}
      value={currentTenant}
      onChange={handleTenantChange}
      clearable={isAdmin}
      searchable
    />
  );
};
